import { useState, useRef, useEffect, useCallback } from "react";
import { useTheme } from "../App.jsx";

// ════════════════════════════════════════════════════════════════════
// Follow the Leader — AI Support Widget
//
// Drop this anywhere in your React app or landing page.
// Sends messages to our own backend (POST /support/chat), which holds
// the Anthropic API key server-side and applies the system prompt —
// the browser never talks to api.anthropic.com or sees the key. Asking for
// a person makes the backend email the transcript to the support inbox
// (`escalated` on the response).
//
// Usage:
//   import SupportWidget from "./SupportWidget";
//   <SupportWidget />
//
// The widget renders as a floating chat bubble (bottom-right).
// Pass position="inline" to embed it in a page section instead.
//
// Colors come from useTheme() (see App.jsx) — this widget must be
// mounted under the same ThemeProvider as the active app shell (see
// main.jsx) so light/dark stays in sync with the rest of the app.
// ════════════════════════════════════════════════════════════════════

const API_BASE = (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) || "http://localhost:8000";

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

// ── suggested questions shown before first message ───────────────────
const SUGGESTED = [
  "What does the conviction score mean?",
  "Why didn't I get an alert?",
  "Does this app place trades for me?",
  "How do I cancel my subscription?",
  "Is this affiliated with Polymarket?",
  "I'd like to talk to a person",
];

// ── call our backend support proxy (never Anthropic directly) ────────
async function callClaude(messages) {
  const response = await fetch(`${API_BASE}/support/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messages, platform: "web" }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.detail || `API error ${response.status}`);
  }

  const data = await response.json();
  return { reply: data.reply || "", escalated: !!data.escalated };
}

// ════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ════════════════════════════════════════════════════════════════════

function TypingIndicator() {
  const C = useTheme();
  return (
    <div style={{ display: "flex", gap: 5, padding: "10px 14px", alignItems: "center" }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: 7, height: 7, borderRadius: "50%",
          background: C.textMuted,
          animation: `ftl-bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
          display: "inline-block",
        }} />
      ))}
    </div>
  );
}

function Message({ msg }) {
  const C = useTheme();
  const isUser = msg.role === "user";
  return (
    <div style={{
      display:       "flex",
      justifyContent: isUser ? "flex-end" : "flex-start",
      marginBottom:  10,
      animation:     "ftl-fadein 180ms ease-out",
    }}>
      {!isUser && (
        <div style={{
          width: 28, height: 28, borderRadius: 8,
          background: C.green,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 13, fontWeight: 700, color: C.bg,
          flexShrink: 0, marginRight: 8, marginTop: 2,
        }}>F</div>
      )}
      <div style={{
        maxWidth:     "78%",
        background:   isUser ? C.greenDim : C.surface,
        border:       `1px solid ${isUser ? C.green + "44" : C.border}`,
        borderRadius: isUser ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
        padding:      "10px 13px",
        fontSize:     14,
        lineHeight:   1.5,
        color:        isUser ? C.green : C.text,
        whiteSpace:   "pre-wrap",
        wordBreak:    "break-word",
      }}>
        {msg.content}
      </div>
    </div>
  );
}

function SuggestedQuestions({ onSelect }) {
  const C = useTheme();
  return (
    <div style={{ padding: "0 12px 12px" }}>
      <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 8, letterSpacing: 0.4, textTransform: "uppercase" }}>
        Common questions
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {SUGGESTED.map(q => (
          <button key={q} onClick={() => onSelect(q)} style={{
            background:   "transparent",
            border:       `1px solid ${C.border}`,
            borderRadius: 10,
            padding:      "8px 12px",
            textAlign:    "left",
            fontSize:     13,
            color:        C.textDim,
            cursor:       "pointer",
            transition:   "border-color 120ms, color 120ms",
            fontFamily:   FONT,
          }}
          onMouseEnter={e => { e.target.style.borderColor = C.green + "66"; e.target.style.color = C.text; }}
          onMouseLeave={e => { e.target.style.borderColor = C.border; e.target.style.color = C.textDim; }}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

function DisclaimerBar() {
  const C = useTheme();
  return (
    <div style={{
      padding:    "7px 14px",
      borderTop:  `1px solid ${C.border}`,
      fontSize:   10.5,
      color:      C.textMuted,
      textAlign:  "center",
      lineHeight: 1.4,
      opacity:    0.8,
    }}>
      Not financial advice · Not affiliated with Polymarket · AI responses may be inaccurate
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// CHAT PANEL
// ════════════════════════════════════════════════════════════════════
function ChatPanel({ onClose, position }) {
  const C = useTheme();
  const [messages,  setMessages]  = useState([]);
  const [input,     setInput]     = useState("");
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [escalated, setEscalated] = useState(false);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  const showSuggested = messages.length === 0;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (!loading) inputRef.current?.focus();
  }, [loading]);

  const send = useCallback(async (text) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    const userMsg = { role: "user", content: trimmed };
    const next    = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      // Send the recent history for context. The server takes at most 50
      // messages, so long chats keep only the last 40, starting on a user turn.
      let recent = next.slice(-40);
      while (recent.length && recent[0].role !== "user") recent = recent.slice(1);
      const apiMessages = recent.map(m => ({ role: m.role, content: m.content }));
      const { reply, escalated: sent } = await callClaude(apiMessages);
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
      if (sent) setEscalated(true);
    } catch (err) {
      // The server's message is user-facing (e.g. the daily limit); network
      // failures get the generic one.
      setError(err?.message && !/^API error|Failed to fetch/.test(err.message)
        ? err.message
        : "Couldn't reach support right now. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  }, [messages, loading]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); }
  };

  const isFloating = position !== "inline";

  return (
    <div style={{
      display:       "flex",
      flexDirection: "column",
      width:         isFloating ? 360 : "100%",
      height:        isFloating ? 520 : 560,
      background:    C.bg,
      border:        `1px solid ${C.border}`,
      borderRadius:  isFloating ? 18 : 16,
      overflow:      "hidden",
      boxShadow:     isFloating ? `0 24px 48px rgba(0,0,0,0.6), 0 0 0 1px ${C.border}` : "none",
      fontFamily:    FONT,
    }}>

      {/* Header */}
      <div style={{
        display:        "flex",
        alignItems:     "center",
        gap:            10,
        padding:        "14px 16px",
        borderBottom:   `1px solid ${C.border}`,
        background:     C.surface,
        flexShrink:     0,
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: 10,
          background: C.green,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 15, fontWeight: 700, color: C.bg, flexShrink: 0,
          position: "relative",
        }}>
          F
          <span style={{
            position: "absolute", bottom: -2, right: -2,
            width: 10, height: 10, borderRadius: "50%",
            background: C.green,
            border: `2px solid ${C.surface}`,
            boxShadow: `0 0 6px ${C.green}`,
          }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Follow the Leader Support</div>
          <div style={{ fontSize: 11.5, color: C.green, display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.green, display: "inline-block", boxShadow: `0 0 6px ${C.green}` }} />
            Online now
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} style={{
            background: "transparent", border: "none",
            color: C.textMuted, fontSize: 20, cursor: "pointer",
            padding: 4, lineHeight: 1, borderRadius: 6,
          }}>×</button>
        )}
      </div>

      {/* Messages */}
      <div style={{
        flex:      1,
        overflowY: "auto",
        padding:   "14px 12px 8px",
        display:   "flex",
        flexDirection: "column",
      }}>
        {/* Welcome message */}
        {showSuggested && (
          <div style={{
            background:   C.surface,
            border:       `1px solid ${C.border}`,
            borderRadius: "14px 14px 14px 4px",
            padding:      "10px 13px",
            fontSize:     14,
            lineHeight:   1.5,
            color:        C.text,
            marginBottom: 14,
            marginLeft:   36,
            animation:    "ftl-fadein 200ms ease-out",
          }}>
            Hey — I'm the Follow the Leader support agent. Ask me anything about the app, how alerts work, your subscription, or how to get the most out of it. If you'd rather talk to a person, just say so and I'll pass your message to the team.
            <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 6 }}>
              Not financial advice. I can't recommend trades.
            </div>
          </div>
        )}

        {messages.map((msg, i) => <Message key={i} msg={msg} />)}

        {loading && (
          <div style={{ display: "flex", marginBottom: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: C.green, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: C.bg, flexShrink: 0, marginRight: 8, marginTop: 2 }}>F</div>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: "14px 14px 14px 4px" }}>
              <TypingIndicator />
            </div>
          </div>
        )}

        {escalated && (
          <div style={{
            background: C.greenDim, border: `1px solid ${C.green}44`,
            borderRadius: 10, padding: "8px 12px",
            fontSize: 13, color: C.text, marginBottom: 8,
          }}>
            Your message is with the Follow the Leader team. They'll reply by email.
          </div>
        )}

        {error && (
          <div style={{
            background: C.redDim, border: `1px solid ${C.red}44`,
            borderRadius: 10, padding: "8px 12px",
            fontSize: 13, color: C.red, marginBottom: 8,
          }}>
            {error}
            <button onClick={() => setError(null)} style={{
              background: "transparent", border: "none", color: C.red,
              cursor: "pointer", fontSize: 13, marginLeft: 8, padding: 0,
              textDecoration: "underline",
            }}>Dismiss</button>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Suggested questions */}
      {showSuggested && <SuggestedQuestions onSelect={send} />}

      {/* Input */}
      <div style={{
        borderTop:  `1px solid ${C.border}`,
        padding:    "10px 12px",
        display:    "flex",
        gap:        8,
        alignItems: "flex-end",
        flexShrink: 0,
        background: C.surface,
      }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Ask a question…"
          rows={1}
          disabled={loading}
          style={{
            flex:       1,
            background: C.bg,
            border:     `1px solid ${input ? C.green + "55" : C.border}`,
            borderRadius: 10,
            color:      C.text,
            fontSize:   14,
            padding:    "9px 12px",
            fontFamily: FONT,
            resize:     "none",
            outline:    "none",
            lineHeight: 1.45,
            maxHeight:  96,
            overflowY:  "auto",
            transition: "border-color 140ms",
          }}
        />
        <button
          onClick={() => send(input)}
          disabled={!input.trim() || loading}
          style={{
            width:        38,
            height:       38,
            borderRadius: 10,
            border:       "none",
            background:   !input.trim() || loading ? C.greenDim : C.green,
            color:        !input.trim() || loading ? C.textMuted : C.bg,
            fontSize:     18,
            cursor:       !input.trim() || loading ? "not-allowed" : "pointer",
            display:      "flex",
            alignItems:   "center",
            justifyContent: "center",
            flexShrink:   0,
            transition:   "background 140ms, color 140ms",
          }}
          aria-label="Send"
        >
          ↑
        </button>
      </div>

      <DisclaimerBar />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// FLOATING TRIGGER BUTTON
// ════════════════════════════════════════════════════════════════════
function TriggerButton({ open, onClick, unread }) {
  const C = useTheme();
  return (
    <button
      onClick={onClick}
      aria-label={open ? "Close support chat" : "Open support chat"}
      style={{
        width:        56,
        height:       56,
        borderRadius: "50%",
        background:   open ? C.surface : C.green,
        border:       `1px solid ${open ? C.border : "transparent"}`,
        color:        open ? C.textMuted : C.bg,
        fontSize:     open ? 24 : 22,
        cursor:       "pointer",
        display:      "flex",
        alignItems:   "center",
        justifyContent: "center",
        boxShadow:    open
          ? "none"
          : `0 4px 20px rgba(0,214,126,0.35), 0 2px 8px rgba(0,0,0,0.4)`,
        transition:   "background 160ms, box-shadow 160ms, transform 120ms",
        transform:    open ? "scale(0.95)" : "scale(1)",
        position:     "relative",
        flexShrink:   0,
      }}
      onMouseEnter={e => { if (!open) e.currentTarget.style.transform = "scale(1.06)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = open ? "scale(0.95)" : "scale(1)"; }}
    >
      {open ? "×" : "?"}
      {!open && unread > 0 && (
        <span style={{
          position:   "absolute",
          top:        -2, right: -2,
          minWidth:   18, height: 18,
          borderRadius: 999,
          background: C.amber,
          color:      C.bg,
          fontSize:   10,
          fontWeight: 700,
          display:    "flex",
          alignItems: "center",
          justifyContent: "center",
          padding:    "0 4px",
          border:     `2px solid ${C.bg}`,
        }}>{unread}</span>
      )}
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════
// ROOT EXPORT
// ════════════════════════════════════════════════════════════════════

/**
 * SupportWidget
 *
 * Props:
 *   position  "floating" (default) | "inline"
 *             floating = fixed bottom-right chat bubble
 *             inline   = renders the panel directly in the DOM flow
 *
 * Examples:
 *   <SupportWidget />                  ← floating bubble
 *   <SupportWidget position="inline" /> ← embedded in a page section
 */
export default function SupportWidget({ position = "floating" }) {
  const [open, setOpen] = useState(false);

  if (position === "inline") {
    return (
      <>
        <style>{KEYFRAMES}</style>
        <ChatPanel position="inline" />
      </>
    );
  }

  return (
    <>
      <style>{KEYFRAMES}</style>
      <div style={{
        position:  "fixed",
        bottom:    24,
        right:     24,
        zIndex:    9999,
        display:   "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap:       12,
      }}>
        {open && (
          <div style={{ animation: "ftl-slideup 200ms cubic-bezier(0.16,1,0.3,1)" }}>
            <ChatPanel onClose={() => setOpen(false)} position="floating" />
          </div>
        )}
        <TriggerButton open={open} onClick={() => setOpen(o => !o)} />
      </div>
    </>
  );
}

// ── keyframes ─────────────────────────────────────────────────────────
const KEYFRAMES = `
  @keyframes ftl-bounce {
    0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
    40%            { transform: scale(1); opacity: 1;   }
  }
  @keyframes ftl-fadein {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0);   }
  }
  @keyframes ftl-slideup {
    from { opacity: 0; transform: translateY(16px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0)    scale(1);    }
  }
  @media (prefers-reduced-motion: reduce) {
    * { animation-duration: 0.01ms !important; }
  }
`;
